/**
 * 보험 타입 공통 상수 정의
 */

/**
 * 보험 타입 맵핑
 */
export const INSURANCE_TYPE_MAP = {
  'nonlife_insu': { text: '장기보험', className: 'bg-blue-100 text-blue-800' },
  'life_insu': { text: '생명보험', className: 'bg-green-100 text-green-800' },
  'car_insu': { text: '자동차보험', className: 'bg-purple-100 text-purple-800' },
  'general_insu': { text: '일반보험', className: 'bg-gray-100 text-gray-800' }
};

/**
 * 보험 타입 그룹 키
 */
export const INSURANCE_TYPE_GROUP_KEY = {
  NONLIFE_LIFE_GENERAL: 'nonlife_life_general',
  CAR: 'car_insu'
};

/**
 * 보험 타입 그룹 라벨
 */
export const INSURANCE_TYPE_GROUP_LABEL = {
  'nonlife_life_general': '장기보험/생명보험/일반보험',
  'car_insu': '자동차보험'
};

/**
 * 보험 타입 이름 맵핑 (전체 공통 - 다른 페이지에서 사용)
 */
export const INSURANCE_TYPE_NAME_MAP = {
  'nonlife_insu': '장기보험',
  'life_insu': '생명보험',
  'car_insu': '자동차보험',
  'general_insu': '일반보험'
};

/**
 * 보험 처리 유형 전용 표시 이름 (프로필 모달, GA 사용자관리 등록/수정 모달에서만 사용)
 */
export const INSURANCE_TYPE_DISPLAY_NAME_MAP = {
  'nonlife_insu': '장기보험',
  'life_insu': '생명보험',
  'car_insu': '자동차보험',
  'general_insu': '일반보험'
};

/**
 * 보험 타입 값
 */
export const INSURANCE_TYPE = {
  NONLIFE: 'nonlife_insu',
  LIFE: 'life_insu',
  CAR: 'car_insu',
  GENERAL: 'general_insu'
};

/**
 * 보험 타입이 자동차보험인지 확인
 * @param {string} insuranceType - 보험 타입
 * @returns {boolean}
 */
export const isCarInsurance = (insuranceType) => {
  return insuranceType === INSURANCE_TYPE.CAR;
};

/**
 * 보험 타입 그룹 키 생성
 * 장기보험, 생명보험, 일반보험은 'nonlife_life_general'로 그룹화
 * 자동차보험은 별도 그룹
 * @param {string} insuranceType - 보험 타입
 * @returns {string} 그룹 키
 */
export const getInsuranceTypeGroupKey = (insuranceType) => {
  if (!insuranceType) return 'unknown';
  if (
    insuranceType === INSURANCE_TYPE.NONLIFE ||
    insuranceType === INSURANCE_TYPE.LIFE ||
    insuranceType === INSURANCE_TYPE.GENERAL
  ) {
    return INSURANCE_TYPE_GROUP_KEY.NONLIFE_LIFE_GENERAL;
  }
  return insuranceType;
};

/** 손해보험(nonlife_insu) 원수사 프로모션에서 선택 가능한 보험유형 code1 */
export const PROMOTION_NONLIFE_INSU_OPTIONS = [
  INSURANCE_TYPE.NONLIFE,
  INSURANCE_TYPE.CAR,
  INSURANCE_TYPE.GENERAL,
];

/**
 * 원수사 companies.insu_type 기준 프로모션 보험유형 선택 목록
 * @param {string|null|undefined} companyInsuType - life_insu | nonlife_insu
 * @param {Array<{code1: string, code1Label?: string}>} codeValues - INSU_TYPE 코드값
 */
export const getPromotionInsuTypeOptions = (companyInsuType, codeValues) => {
  const codes = Array.isArray(codeValues) ? codeValues : [];
  if (companyInsuType === INSURANCE_TYPE.LIFE) {
    return codes.filter((c) => c.code1 === INSURANCE_TYPE.LIFE);
  }
  if (companyInsuType === INSURANCE_TYPE.NONLIFE) {
    return codes.filter((c) => PROMOTION_NONLIFE_INSU_OPTIONS.includes(c.code1));
  }
  return [];
};

/** 원수사 변경 시 프로모션 보험유형 자동/유지 결정 */
export const resolvePromotionInsuTypeOnCompanyChange = (companyInsuType, currentInsuType) => {
  if (companyInsuType === INSURANCE_TYPE.LIFE) {
    return INSURANCE_TYPE.LIFE;
  }
  if (companyInsuType === INSURANCE_TYPE.NONLIFE) {
    return PROMOTION_NONLIFE_INSU_OPTIONS.includes(currentInsuType) ? currentInsuType : '';
  }
  return '';
};

/** 생명보험 원수사는 보험유형 고정 */
export const isPromotionInsuTypeFixed = (companyInsuType) =>
  companyInsuType === INSURANCE_TYPE.LIFE;

/** 원수사 선택 목록: companies.insu_type → 생명/손해 그룹 키 (nonlife_insu는 life_insu 포함 전 검사) */
export const resolveCompanyInsuTypeGroupKey = (insuType) => {
  const normalized = `,${String(insuType || '').toLowerCase().replace(/\s/g, '')},`;
  if (normalized.includes(',nonlife_insu,')) return INSURANCE_TYPE.NONLIFE;
  if (normalized.includes(',life_insu,')) return INSURANCE_TYPE.LIFE;
  return null;
};

export const COMPANY_INSU_TYPE_GROUP_ORDER = [
  { key: INSURANCE_TYPE.LIFE, label: '생명보험' },
  { key: INSURANCE_TYPE.NONLIFE, label: '손해보험' },
];

const isCompanyNameKoreanLeading = (name) => /[\uAC00-\uD7A3]/.test(String(name || '').charAt(0));

/** 원수사명: 한글 먼저·가나다순, 이후 영문·알파벳순 */
export const compareCompanyNameKoThenEnAsc = (a, b) => {
  const nameA = a.companyName || '';
  const nameB = b.companyName || '';
  const isKoreanA = isCompanyNameKoreanLeading(nameA);
  const isKoreanB = isCompanyNameKoreanLeading(nameB);
  if (isKoreanA && !isKoreanB) return -1;
  if (!isKoreanA && isKoreanB) return 1;
  return nameA.localeCompare(nameB, isKoreanA ? 'ko' : 'en');
};

/** 원수사명: 영문 먼저·알파벳순, 이후 한글·가나다순 */
export const compareCompanyNameEnThenKoAsc = (a, b) => {
  const nameA = a.companyName || '';
  const nameB = b.companyName || '';
  const isKoreanA = isCompanyNameKoreanLeading(nameA);
  const isKoreanB = isCompanyNameKoreanLeading(nameB);
  if (!isKoreanA && isKoreanB) return -1;
  if (isKoreanA && !isKoreanB) return 1;
  return nameA.localeCompare(nameB, isKoreanA ? 'ko' : 'en');
};

export const COMPANY_INSU_TYPE_TREE_GROUP_ORDER = [
  { key: INSURANCE_TYPE.NONLIFE, label: '손해보험' },
  { key: INSURANCE_TYPE.LIFE, label: '생명보험' },
];

export const insurerTreeTypeKey = (typeKey) => `type:${typeKey}`;
export const insurerTreeCompanyKey = (companyId) => `company:${companyId}`;

/**
 * 원수사 계층트리용: 손해보험/생명보험 그룹 + 영문·한글 오름차순
 * @param {Array<{id: number|string, companyName?: string, insuType?: string}>} companies
 */
export const groupCompaniesByInsuTypeForTree = (companies) => {
  const list = Array.isArray(companies) ? companies : [];
  const buckets = {
    [INSURANCE_TYPE.LIFE]: [],
    [INSURANCE_TYPE.NONLIFE]: [],
    other: [],
  };

  list.forEach((company) => {
    const groupKey = resolveCompanyInsuTypeGroupKey(company.insuType);
    if (groupKey === INSURANCE_TYPE.LIFE) {
      buckets[INSURANCE_TYPE.LIFE].push(company);
    } else if (groupKey === INSURANCE_TYPE.NONLIFE) {
      buckets[INSURANCE_TYPE.NONLIFE].push(company);
    } else {
      buckets.other.push(company);
    }
  });

  const groups = COMPANY_INSU_TYPE_TREE_GROUP_ORDER.map(({ key, label }) => ({
    key,
    label,
    companies: [...buckets[key]].sort(compareCompanyNameEnThenKoAsc),
  }));

  if (buckets.other.length > 0) {
    groups.push({
      key: 'other',
      label: '기타',
      companies: [...buckets.other].sort(compareCompanyNameEnThenKoAsc),
    });
  }

  return groups;
};

/**
 * 원수사 선택 <select>용: 보험유형(생명/손해)별 optgroup + 가나다순
 * @param {Array<{id: number|string, companyName?: string, insuType?: string}>} companies
 */
export const groupCompaniesByInsuTypeForSelect = (companies) => {
  const list = Array.isArray(companies) ? companies : [];
  const buckets = {
    [INSURANCE_TYPE.LIFE]: [],
    [INSURANCE_TYPE.NONLIFE]: [],
    other: [],
  };

  list.forEach((company) => {
    const groupKey = resolveCompanyInsuTypeGroupKey(company.insuType);
    if (groupKey === INSURANCE_TYPE.LIFE) {
      buckets[INSURANCE_TYPE.LIFE].push(company);
    } else if (groupKey === INSURANCE_TYPE.NONLIFE) {
      buckets[INSURANCE_TYPE.NONLIFE].push(company);
    } else {
      buckets.other.push(company);
    }
  });

  const groups = COMPANY_INSU_TYPE_GROUP_ORDER.map(({ key, label }) => ({
    key,
    label,
    companies: [...buckets[key]].sort(compareCompanyNameKoThenEnAsc),
  }));

  if (buckets.other.length > 0) {
    groups.push({
      key: 'other',
      label: '기타',
      companies: [...buckets.other].sort(compareCompanyNameKoThenEnAsc),
    });
  }

  return groups;
};
